import {
  LightningFeatureProperties,
  MapboxFeature,
  MapClickGetPois,
  Poi,
} from 'features/mapping/mapping.types';
import { LightningPoi } from 'features/mapping/components/dialogs/PoiDialog/PoiDialog.types';
import { Point } from 'geojson';
import { fromUnixTime } from 'date-fns';
import { sortedIndex, sortedLastIndex } from 'es-toolkit/compat';
import {
  LngLatBounds,
  MapEventOf,
  MapGeoJSONFeature,
} from 'shared/map-exports';
import {
  getActiveTimelineTime,
  TimelineClock,
} from 'features/mapping/state/useTimelineClockState';
import { MapMode } from 'hooks/useMapMode';
import {
  LIGHTNING_TIMELINE_WINDOW_SEC,
  RADAR_TIMELINE_WINDOW_SEC,
  SOURCE_ID,
} from './LightningLayer.constants';
import {
  LightningTimeFilter,
  StrikeCounts,
  StrikeTimes,
  TimeWindow,
} from './LightningLayer.types';

const getLightningMetadata = (
  feature: MapboxFeature<LightningFeatureProperties>,
): LightningPoi => {
  const { coordinates } = feature.geometry as Point;
  const latitude = coordinates[1];
  const longitude = coordinates[0];
  const props = feature.properties;
  const flashTime = fromUnixTime(props.flash_time);
  return { id: feature.id, latitude, longitude, flashTime };
};

export const getSelectedLightningId = (selectedPoi?: Poi): string => {
  return selectedPoi?.type === 'lightning' && selectedPoi.id
    ? String(selectedPoi.id)
    : '';
};

export const lightningClickHandler: MapClickGetPois<
  LightningFeatureProperties
> = (geoJsonFeatures) => {
  const feature = geoJsonFeatures[0];
  return [
    {
      type: 'lightning',
      id: feature.id,
      metadata: getLightningMetadata(feature),
    },
  ];
};

/** Which strikes to show in each map mode, given the timeline clock. */
export const getLightningTimeFilter = (
  mapMode: MapMode,
  clock: TimelineClock,
  nowMs: number,
): LightningTimeFilter => {
  const timeMs = getActiveTimelineTime(mapMode, clock);

  switch (mapMode) {
    case 'default':
      return null;
    case 'lightning':
      // Hidden until the first frame, so the whole lookback doesn't flash
      if (timeMs === null) return 'hidden';
      return { endMs: timeMs, durationSec: LIGHTNING_TIMELINE_WINDOW_SEC };
    case 'radar':
      // Hidden until the first frame, so stale lightning timeline tiles don't flash
      if (timeMs === null) return 'hidden';
      // Futurecast frame
      if (timeMs > nowMs) return 'hidden';
      return { endMs: timeMs, durationSec: RADAR_TIMELINE_WINDOW_SEC };
    default:
      // Other timeline layers
      return 'hidden';
  }
};

export const getTimeWindowBoundsSec = ({
  endMs,
  durationSec,
}: TimeWindow): { startSec: number; endSec: number } => {
  const endSec = Math.floor(endMs / 1000);
  return { startSec: endSec - durationSec, endSec };
};

/** Strike times inside `bounds`, deduped by feature id. */
export const collectStrikeTimes = (
  features: MapGeoJSONFeature[],
  bounds: LngLatBounds,
): StrikeTimes => {
  const seen = new Set<string | number>();
  const times: StrikeTimes = { positive: [], negative: [], total: [] };

  features.forEach((feature) => {
    const { id, geometry, properties } = feature;
    const flashTime: unknown = properties?.flash_time;
    if (id === undefined || seen.has(id)) return;
    if (geometry.type !== 'Point' || typeof flashTime !== 'number') return;
    const [lng, lat] = geometry.coordinates;
    if (!bounds.contains([lng, lat])) return;

    seen.add(id);
    if (properties?.flash_polarity === 'P') times.positive.push(flashTime);
    if (properties?.flash_polarity === 'N') times.negative.push(flashTime);
    times.total.push(flashTime);
  });

  // Sorted for the binary search in countInRange
  const ascending = (a: number, b: number): number => a - b;
  return {
    positive: times.positive.sort(ascending),
    negative: times.negative.sort(ascending),
    total: times.total.sort(ascending),
  };
};

// Takes a sorted array and counts the values in [start, end], both ends
// inclusive to match the map filter (`>=` start, `<=` end). `sortedIndex` is
// the first index with a value >= start and `sortedLastIndex` the first with a
// value > end, so the values between them are the ones in range.
const countInRange = (sorted: number[], start: number, end: number): number =>
  sortedLastIndex(sorted, end) - sortedIndex(sorted, start);

export const countStrikesInWindow = (
  times: StrikeTimes,
  window: TimeWindow,
): StrikeCounts => {
  const { startSec, endSec } = getTimeWindowBoundsSec(window);
  return {
    positive: countInRange(times.positive, startSec, endSec),
    negative: countInRange(times.negative, startSec, endSec),
    total: countInRange(times.total, startSec, endSec),
  };
};

/**
 * True for a `sourcedata` event after which the lightning tiles are loaded.
 * Skips two events that report loaded too early after a tile URL swap:
 * - `metadata`, while the old tiles are still in place
 * - aborted old tiles, whose callbacks still fire after mapbox clears the
 *   source and before it requests the new tiles
 */
export const isLightningSourceLoadedEvent = (
  event: MapEventOf<'sourcedata'>,
): boolean =>
  event.sourceId === SOURCE_ID &&
  event.sourceDataType !== 'metadata' &&
  !event.tile?.aborted &&
  event.target.isSourceLoaded(SOURCE_ID);
